function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function coerceAssetRowId(raw: unknown): string | null {
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t.length > 0 ? t : null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/**
 * Asset identity keys for list/book/detail — excludes `facility_id`, which on search/list rows
 * often refers to an amenity catalog entry shared by many assets (not the bookable asset id).
 */
const ASSET_ROW_PRIMARY_ID_KEYS = [
  'id',
  'Id',
  'UUID',
  'uuid',
  'asset_id',
  'assetId',
  'AssetId',
  '_id',
] as const;

const ASSET_ROW_MUTATION_ID_KEYS = [
  'asset_id',
  'assetId',
  'AssetId',
  'id',
  'Id',
  'UUID',
  'uuid',
  '_id',
] as const;

const NESTED_ASSET_ENVELOPE_KEYS = ['asset', 'Asset', 'facility', 'Facility'] as const;

function pickAssetRowPrimaryIdFromRow(row: Record<string, unknown>): string | null {
  for (const key of ASSET_ROW_PRIMARY_ID_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(row, key)) {
      continue;
    }
    const picked = coerceAssetRowId(row[key]);
    if (picked) {
      return picked;
    }
  }
  return null;
}

function pickAssetRowMutationIdFromRow(row: Record<string, unknown>): string | null {
  for (const key of ASSET_ROW_MUTATION_ID_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(row, key)) {
      continue;
    }
    const picked = coerceAssetRowId(row[key]);
    if (picked) {
      return picked;
    }
  }
  return null;
}

/**
 * First usable id for list/detail/booking. Does not treat amenity `facility_id` as the asset id.
 */
export function assetRecordPrimaryId(data: Record<string, unknown>): string | null {
  const fromSelf = pickAssetRowPrimaryIdFromRow(data);
  if (fromSelf) {
    return fromSelf;
  }

  for (const key of NESTED_ASSET_ENVELOPE_KEYS) {
    const nested = data[key];
    if (!isPlainObject(nested)) {
      continue;
    }
    const fromNested = pickAssetRowPrimaryIdFromRow(nested);
    if (fromNested) {
      return fromNested;
    }
  }

  return null;
}

/**
 * Id for POST/DELETE API routes (`/favourites/:id`, `/assets/:id`, …).
 * Prefers `asset_id` over generic `id` — search/list rows often carry both and `id` may be stale.
 */
export function assetRecordMutationId(data: Record<string, unknown>): string | null {
  const fromSelf = pickAssetRowMutationIdFromRow(data);
  if (fromSelf) {
    return fromSelf;
  }

  for (const key of NESTED_ASSET_ENVELOPE_KEYS) {
    const nested = data[key];
    if (!isPlainObject(nested)) {
      continue;
    }
    const fromNested = pickAssetRowMutationIdFromRow(nested);
    if (fromNested) {
      return fromNested;
    }
  }

  return null;
}

export function isUuidLikeAssetId(value: string): boolean {
  const trimmed = value.trim();
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed) ||
    /^[0-9a-f]{32}$/i.test(trimmed)
  );
}

/** Preferred single id for GET/POST `/assets/:id` and `/favourites/:id`. */
export function assetRecordApiLookupId(data: Record<string, unknown>): string | null {
  const candidates = assetRecordApiLookupIdCandidates(data);
  return candidates[0] ?? null;
}

/** Ordered ids to try against the assets API — numeric/legacy ids before UUIDs. */
export function assetRecordApiLookupIdCandidates(
  data: Record<string, unknown>,
  ...extraHints: string[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (raw: unknown) => {
    const id = coerceAssetRowId(raw);
    if (!id) {
      return;
    }
    const key = id.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(id);
  };

  add(assetRecordMutationId(data));
  for (const hint of extraHints) {
    add(hint);
  }
  add(assetRecordPrimaryId(data));
  for (const key of ['asset_id', 'assetId', 'AssetId', 'id', 'Id', 'UUID', 'uuid', '_id'] as const) {
    add(data[key]);
  }

  return out.sort((a, b) => {
    const aUuid = isUuidLikeAssetId(a);
    const bUuid = isUuidLikeAssetId(b);
    if (aUuid === bUuid) {
      return 0;
    }
    return aUuid ? 1 : -1;
  });
}

/** Stable React key / dedupe helper when {@link assetRecordPrimaryId} is absent. */
export function assetRecordStableListKey(asset: Record<string, unknown>, index: number): string {
  const id = assetRecordPrimaryId(asset);
  if (id) {
    return id;
  }
  const address =
    (typeof asset.address === 'string' && asset.address.trim()) ||
    (typeof asset.street === 'string' && asset.street.trim()) ||
    (typeof asset.city === 'string' && asset.city.trim()) ||
    '';
  if (address) {
    return `row-${address.toLowerCase()}-${index}`;
  }
  return `idx-${index}`;
}
